import { gql } from "urql";

// Get AiPersonality by id query

export const aiPersonalityQuery = gql`
    query AiPersonalityQuery($aiPersonalityId:Int!) {
        aiPersonality(aiPersonalityId:$aiPersonalityId) {
            id
            publicId
            name
            externalName
            temperature
            systemPrompt
            includeRetrieval
            includeBrainstorm
            includeMissingInformation
            epsilon
            tags
            languages
            model
            useCache
            showInChat
            reasoningEffort
            verbosity
            responseFormat
            maxIterations
            tuneThreads {
                edges {
                    node {
                        id
                        referenceAnswer
                        llmJudge
                        label
                        tuneThreadItems {
                            edges {
                                node {
                                    id
                                    type
                                    role
                                    content
                                    imageB64
                                    imageName
                                    toolCallId
                                    toolName
                                    toolArguments
                                    reasoningId
                                    reasoningSummary
                                    encryptedReasoning
                                    context
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export type * from "./__generated__/ai-personality-query";

// Add TuneThread mutation

export const addMutation = gql`
    mutation TuneThreadAddMutation($data:[TuneThreadCreateInput!]!) {
        tuneThreadCreate(data:$data) {
            id
            aiPersonality {
                id
            }
            referenceAnswer
            llmJudge
            label
            tuneThreadItems {
                edges {
                    node {
                        id
                        type
                        role
                        content
                        imageB64
                        imageName
                        toolCallId
                        toolName
                        toolArguments
                        reasoningId
                        reasoningSummary
                        encryptedReasoning
                        context
                    }
                }
            }
        }
    }
`;

export type * from "./__generated__/tune-thread-add-mutation";

// Edit TuneThread mutation

export const editMutation = gql`
    mutation TuneThreadEditMutation($data:TuneThreadUpdateInput!) {
        tuneThreadUpdate(data:$data) {
            id
            aiPersonality {
                id
            }
            referenceAnswer
            llmJudge
            label
        }
    }
`;

export type * from "./__generated__/tune-thread-edit-mutation";

// Set label mutation

export const setLabelMutation = gql`
    mutation TuneThreadSetLabelMutation($data: TuneThreadSetLabelInput!) {
        tuneThreadSetLabel(data: $data) {
            id
            label
        }
    }
`;

export type * from "./__generated__/tune-thread-set-label-mutation";

// Delete TuneThread mutation

export const deleteMutation = gql`
    mutation TuneThreadDeleteMutation($tuneThreadId:Int!) {
        tuneThreadDelete(tuneThreadId:$tuneThreadId) {
            id
            aiPersonality {
                id
            }
            referenceAnswer
            llmJudge
            label
            tuneThreadItems {
                edges {
                    node {
                        id
                        type
                        role
                        content
                        imageB64
                        imageName
                        toolCallId
                        toolName
                        toolArguments
                        reasoningId
                        reasoningSummary
                        encryptedReasoning
                        context
                    }
                }
            }
        }
    }
`;

export type * from "./__generated__/tune-thread-delete-mutation";

// Clone TuneThread mutation

export const cloneMutation = gql`
    mutation TuneThreadCloneMutation($data: TuneThreadCloneInput!) {
        tuneThreadClone(data: $data) {
            id
            aiPersonality {
                id
            }
            referenceAnswer
            llmJudge
            label
            tuneThreadItems {
                edges {
                    node {
                        id
                        type
                        role
                        content
                        imageB64
                        imageName
                        toolCallId
                        toolName
                        toolArguments
                        reasoningId
                        reasoningSummary
                        encryptedReasoning
                        context
                    }
                }
            }
        }
    }
`;

export type * from "./__generated__/tune-thread-clone-mutation";

// Add TuneThreadItem mutation

export const addItemMutation = gql`
    mutation TuneThreadAddItemMutation($data:TuneThreadAddItemInput!) {
        tuneThreadAddItem(data:$data) {
            id
            aiPersonality {
                id
            }
            referenceAnswer
            llmJudge
            label
            tuneThreadItems {
                edges {
                    node {
                        id
                        type
                        role
                        content
                        imageB64
                        imageName
                        toolCallId
                        toolName
                        toolArguments
                        reasoningId
                        reasoningSummary
                        encryptedReasoning
                        context
                    }
                }
            }
        }
    }
`;

export type * from "./__generated__/tune-thread-add-item-mutation";

// Delete TuneThreadItem mutation

export const deleteItemMutation = gql`
    mutation TuneThreadDeleteItemMutation($data:TuneThreadRemoveItemInput!) {
        tuneThreadRemoveItem(data:$data) {
            id
            aiPersonality {
                id
            }
            referenceAnswer
            llmJudge
            label
            tuneThreadItems {
                edges {
                    node {
                        id
                        type
                        role
                        content
                        imageB64
                        imageName
                        toolCallId
                        toolName
                        toolArguments
                        reasoningId
                        reasoningSummary
                        encryptedReasoning
                        context
                    }
                }
            }
        }
    }
`;

export type * from "./__generated__/tune-thread-delete-item-mutation";

// Update TuneThreadItem mutation

export const updateItemMutation = gql`
    mutation TuneThreadUpdateItemMutation($data:TuneThreadUpdateItemInput!) {
        tuneThreadUpdateItem(data:$data) {
            id
            type
            role
            content
            imageB64
            imageName
            toolCallId
            toolName
            toolArguments
            reasoningId
            reasoningSummary
            encryptedReasoning
            context
        }
    }
`;

export type * from "./__generated__/tune-thread-update-item-mutation";

// Reorder TuneThreadItems mutation

export const reorderItemsMutation = gql`
    mutation TuneThreadReorderItemsMutation($data:TuneThreadReorderItemsInput!) {
        tuneThreadReorderItems(data: $data) {
            id
            aiPersonality {
                id
            }
            referenceAnswer
            llmJudge
            label
            tuneThreadItems {
                edges {
                    node {
                        id
                        type
                        role
                        content
                        imageB64
                        imageName
                        toolCallId
                        toolName
                        toolArguments
                        reasoningId
                        reasoningSummary
                        encryptedReasoning
                        context
                    }
                }
            }
        }
    }
`;

export type * from "./__generated__/tune-thread-reorder-items-mutation";

// Ask AI mutation

export const askAiMutation = gql`
    mutation AskAiMutation($data:TuneThreadAskAiInput!) {
        tuneThreadAskAi(data:$data) {
            type
            role
            content
            imageB64
            imageName
            toolCallId
            toolArguments
            toolName
            context
            retrievalContext {
                keywordQuery
                semanticQuery
                hydePassage
                chunks
                avgRelevance
                documentNames
            }
            readContext {
                documentId
                documentName
                startPart
                endPart
                totalParts
            }
            brainstormContext {
                what
                count
                candidates
                probabilities
                selectedIndices
                epsilon
            }

            reasoningId
            reasoningSummary
            encryptedReasoning
        }
    }
`;

export type * from "./__generated__/ask-ai-mutation";

// LLM Judge mutation

export const askAiJudgeMutation = gql`
    mutation AskAiJudgeMutation($data:TuneThreadAskAiJudgeInput!) {
        tuneThreadAskAiJudge(data:$data) {
            reasoning
            score
        }
    }
`;

export type * from "./__generated__/ask-ai-judge-mutation";

// AI Suggest Improvement mutation

export const suggestImprovementMutation = gql`
    mutation TuneThreadAiSuggestImprovementMutation(
        $currentInstructions: String!
        $manualNote: String
        $threads: [TuneThreadSuggestInput!]!
    ) {
        tuneThreadAiSuggestImprovement(
            currentInstructions: $currentInstructions
            manualNote: $manualNote
            threads: $threads
        )
    }
`;

export type * from "./__generated__/tune-thread-ai-suggest-improvement-mutation";
